import React, { useState, useEffect, useRef } from 'react';
import * as Styled from './MParagraph.styles';
import clsx from 'clsx';

import { MParagraphProps, MParagraphAlignment, MParagraphDEFAULT_I18N } from './MParagraph.types';
import { ABody, ABodySize, ABodyColor } from '../../atoms/ABody';
import { ALinkStandalone, ALinkStandaloneSize } from '../../atoms/ALinkStandalone';

const COMPONENT_NAME = 'm-web-ui-kit-MParagraph';
const TEST_ID = 'web-ui-kit-MParagraph';

export const MParagraph: React.FC<MParagraphProps> = ({
  alignment = MParagraphAlignment.Left,
  truncated = false,
  truncateLines = 3,
  expanded = false,
  bodySize: bodySizeProp = ABodySize.S,
  bodyColor: bodyColorProp = ABodyColor.Default,
  truncationLinkSize: truncationLinkSizeProp = ALinkStandaloneSize.Small,
  children,
  aBody,
  aLinkStandalone,
  className,
  i18nProperties,
}) => {
  const [isExpanded, setExpanded] = useState(truncated ? expanded : true);
  const [showTruncationControl, setShowTruncationControl] = useState(truncated);
  const fullText = children;
  const truncateClass = `m-ellipsis-${truncateLines > 1 ? Math.min(truncateLines, 11) + 'lines' : '1line'}`;
  const paragraphRef = useRef<HTMLDivElement>(null);

  // Prefer new grouped props and fall back to legacy props for backwards compatibility.
  const bodySize = aBody?.size ?? bodySizeProp;
  const bodyColor = aBody?.color ?? bodyColorProp;
  const bodyText = aBody?.bodyText ?? fullText ?? '';
  const linkSize = aLinkStandalone?.size ?? truncationLinkSizeProp;
  const linkText = aLinkStandalone?.text ?? i18nProperties?.seeMore ?? MParagraphDEFAULT_I18N.seeMore;
  const linkExternalLink = aLinkStandalone?.externalLink ?? false;

  function manageTruncationControlVisibility(isTruncated: boolean) {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const { offsetHeight, scrollHeight } = paragraphRef.current ?? {};
        if (!isTruncated) {
          setShowTruncationControl(false);
          return;
        }
        if (!offsetHeight || !scrollHeight) {
          return;
        }

        const isOverflowing = offsetHeight < scrollHeight;
        setShowTruncationControl(!isExpanded && isOverflowing);
      });
    });
  }

  function handleResize() {
    manageTruncationControlVisibility(truncated);
  }

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  });

  useEffect(() => {
    setExpanded(!truncated);
    manageTruncationControlVisibility(truncated);
  }, [truncated]);

  useEffect(() => {
    setExpanded(truncated ? expanded : true);
  }, [expanded]);

  function handleClick(e: React.MouseEvent<HTMLAnchorElement>) {
    e.preventDefault();
    setExpanded(!isExpanded);
  }

  return (
    <Styled.Wrapper
      data-component-name={COMPONENT_NAME}
      data-testid={TEST_ID}
      alignment={alignment}
      className={className}
    >
      <Styled.ParagraphContent ref={paragraphRef} className={clsx(!isExpanded && truncateClass)}>
        <ABody bodyText={bodyText} size={bodySize} color={bodyColor} />
      </Styled.ParagraphContent>
      {showTruncationControl && (
        <Styled.TruncationControl>
          <ALinkStandalone onClick={handleClick} href="#" size={linkSize} externalLink={linkExternalLink}>
            {isExpanded ? i18nProperties?.seeLess ?? MParagraphDEFAULT_I18N.seeLess : linkText}
          </ALinkStandalone>
        </Styled.TruncationControl>
      )}
    </Styled.Wrapper>
  );
};

export { MParagraphAlignment, MParagraphDEFAULT_I18N, TEST_ID as M_PARAGRAPH_TEST_ID };
